// src/interactiveUGC/components/InteractiveEditor.jsx
import * as React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Fab from '@mui/material/Fab';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ShareIcon from '@mui/icons-material/Share';
import InteractiveChapterPreviewLauncher from './InteractiveChapterPreviewLauncher';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  ReactFlow, Background, Controls, MiniMap,
  useNodesState, useEdgesState, addEdge, useReactFlow, ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';

import SceneNode from './SceneComponent';

import { getInteractiveStoryChapter, saveInteractiveScene, getCharacters, updateInteractiveChapterTitle, createFreeInteractiveScene, deleteInteractiveScene, validateInteractiveChapter } from '../../utils/apis/interactiveStoriesAPI';

import { t } from '@lingui/macro';

// ====== minimal helpers / constants ======
export const GraphCtx = React.createContext(null);

const newId = (() => { let i = 1; return (p = 'S') => `${p}${i++}`; })();
const makeScene = ({ title, summary } = {}) => ({ title: title ?? t`New Scene`, summary: summary ?? '' });

const nodeTypes = { scene: SceneNode };
const MAX_CHILDREN = 3;

async function layoutWithELK(nodes, edges, direction = 'DOWN') {
  const elk = new ELK();

  // Keep only edges between existing nodes
  const nodeIds = new Set(nodes.map(n => n.id));
  const cleanEdges = edges
    .filter(e => nodeIds.has(e.source) && nodeIds.has(e.target))
    .map(e => ({ id: e.id, source: e.source, target: e.target }));

  // Degrees → detect "root" nodes (no incoming edges).
  // These include the main Start scene and any disconnected roots.
  const inDeg = new Map();
  nodeIds.forEach(id => { inDeg.set(id, 0); });
  cleanEdges.forEach(e => {
    inDeg.set(e.target, (inDeg.get(e.target) || 0) + 1);
  });
  const rootIds = [...nodeIds].filter(id => (inDeg.get(id) || 0) === 0);

  // Single top anchor: keeps all roots on the first layer
  const ANCHOR_TOP = '__ELK_ANCHOR_TOP__';

  const elkChildren = [
    ...nodes.map(n => ({
      id: n.id,
      width: n.width ?? 200,
      height: n.height ?? 80,
    })),
    { id: ANCHOR_TOP, width: 1, height: 1 },
  ];

  const elkEdges = [
    ...cleanEdges.map(e => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
    // Top anchor -> all "root" nodes (Start + any disconnected roots)
    ...rootIds.map((sid, i) => ({
      id: `__top->${sid}__${i}`,
      sources: [ANCHOR_TOP],
      targets: [sid],
    })),
  ];


  const graph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': direction,
      'elk.layered.spacing.nodeNodeBetweenLayers': '48',
      'elk.spacing.nodeNode': '28',
      'elk.layered.spacing.edgeNodeBetweenLayers': '24',
      'elk.edgeRouting': 'POLYLINE',
      'elk.layered.nodePlacement.favorStraightEdges': 'true',
      'elk.layered.mergeEdges': 'true',
      'elk.layered.crossingMinimization.semiInteractive': 'true',
      'elk.layered.considerModelOrder': 'true',
      'elk.layered.layering.strategy': 'NETWORK_SIMPLEX',
    },
    children: elkChildren,
    edges: elkEdges,
  };

  const res = await elk.layout(graph);

  // Positions excluding anchors
  // Positions excluding anchor
  const placed = (res.children || []).filter(c => c.id !== ANCHOR_TOP);

  // --- Horizontal recentering: center graph around X = 0
  const minX = Math.min(...placed.map(c => c.x));
  const maxX = Math.max(...placed.map(c => c.x + (c.width || 0)));
  const cx = (minX + maxX) / 2;
  const pos = Object.fromEntries(
    placed.map(c => [c.id, { x: c.x - cx, y: c.y }])
  );

  return nodes.map(n => ({
    ...n,
    position: pos[n.id] ?? n.position,
  }));
}

function getOrderedChildren(scene, scenesById, edgesBySource) {
  const explicit = Array.isArray(scene?.children) ? scene.children.map(String) : null;
  if (explicit && explicit.length) return explicit;
  const cand = (edgesBySource.get(String(scene?.id)) || []).map(e => String(e.target ?? e.to ?? e.dest));
  if (!cand.length) return [];
  return [...cand].sort((a, b) => {
    const sa = scenesById.get(a) || {};
    const sb = scenesById.get(b) || {};
    const ia = Number.isFinite(sa.index) ? sa.index : Number.MAX_SAFE_INTEGER;
    const ib = Number.isFinite(sb.index) ? sb.index : Number.MAX_SAFE_INTEGER;
    if (ia !== ib) return ia - ib;
    return String(a).localeCompare(String(b));
  });
}

export default function InteractiveEditor({ storyID, chapterID }) {
  return (
    <ReactFlowProvider>
      <InteractiveEditorInner storyID={storyID} chapterID={chapterID} />
    </ReactFlowProvider>
  );
}

function InteractiveEditorInner({ storyID, chapterID }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [view, setView] = React.useState('graph');
  const [characters, setCharacters] = React.useState([]);
  const [charsError, setCharsError] = React.useState('');
  const [chapterTitle, setChapterTitle] = React.useState('');
  const [serverTitle, setServerTitle] = React.useState('');
  const [savingTitle, setSavingTitle] = React.useState(false);
  const [titleError, setTitleError] = React.useState('');

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');
  const [emptySource, setEmptySource] = React.useState(false);

  // ─── Validation state ─────────────────────────────────────────────────────────
  const [validationResult, setValidationResult] = React.useState(null);
  // shape: {
  //   perScene: { [sceneId]: { status: 'ok'|'warning'|'blocking', messages: string[] } },
  //   canPublish: boolean,
  //   blockingCount: number,
  //   warningCount: number,
  //   message: string,
  // }
  const [validationRunning, setValidationRunning] = React.useState(false);
  const [validationError, setValidationError] = React.useState('');
  const resetValidationUI = React.useCallback(() => {
    setValidationResult(null);
    setValidationError('');
  }, []);

  const [pickState, setPickState] = React.useState({
    active: false,
    fromId: null,
    onPick: null,
    onCancel: null,
    eligible: new Set(),
    ineligible: new Set(),
  });

  const rf = useReactFlow();
  const connectingFrom = React.useRef(null);
  const defaultEdgeOptions = React.useMemo(() => ({ type: 'default' }), []);

  React.useEffect(() => {
    let cancelled = false;

    // Any time story/chapter changes, previous validation is stale
    resetValidationUI();

    async function run() {
      if (!storyID || !chapterID) return;

      try {
        setLoading(true);
        setLoadError('');

        const chapter = await getInteractiveStoryChapter(storyID, chapterID);
        if (cancelled) return;

        const title = chapter?.title ?? t`Chapter ${chapterID}`;
        setChapterTitle(title);
        setServerTitle(title);


        const { nodes: nn, edges: ee } = mapChapterToGraph(chapter);
        if (cancelled) return;

        if (!nn.length) {
          // chapter exists but has no scenes yet
          setNodes([]);
          setEdges([]);
          return;
        }

        const laidOut = await layoutWithELK(nn, ee, 'DOWN');
        if (cancelled) return;

        setNodes(laidOut);
        setEdges(ee);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || t`Failed to load chapter.`);
          setNodes([]);
          setEdges([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [storyID, chapterID, setNodes, setEdges, resetValidationUI]);



  // Re-run ELK layout without touching the viewport
  const reLayout = React.useCallback(
    async () => {
      const laidOut = await layoutWithELK(nodes, edges, 'DOWN');
      setNodes(laidOut);
      // no rf.fitView here – user keeps their current pan/zoom
    },
    [nodes, edges, setNodes]
  );

  const reLayoutWith = React.useCallback(
    async (nextNodes, nextEdges) => {
      const laidOut = await layoutWithELK(nextNodes, nextEdges, 'DOWN');
      setNodes(laidOut);
      setEdges(nextEdges);
      // again: no rf.fitView – don't recentre on every change
    },
    [setNodes, setEdges]
  );

  // outgoing edges map for counts/limits
  const outgoing = React.useMemo(() => {
    const map = new Map();
    edges.forEach(e => {
      const arr = map.get(e.source) || [];
      arr.push(e);
      map.set(e.source, arr);
    });
    return map;
  }, [edges]);

  const incoming = React.useMemo(() => {
    const map = new Map();
    edges.forEach(e => {
      const arr = map.get(e.target) || [];
      arr.push(e);
      map.set(e.target, arr);
    });
    return map;
  }, [edges]);

  const getParentCount = React.useCallback(
    (id) => (incoming.get(id) || []).length,
    [incoming]
  );


  const getChildCount = React.useCallback((fromId) => (outgoing.get(fromId) || []).length, [outgoing]);
  const canAddChildFrom = React.useCallback((fromId) => getChildCount(fromId) < MAX_CHILDREN, [getChildCount]);

  const refreshFromBackend = React.useCallback(async () => {
    if (!storyID || !chapterID) return;

    const chapter = await getInteractiveStoryChapter(storyID, chapterID);
    const { nodes: nn, edges: ee } = mapChapterToGraph(chapter);

    if (!nn.length) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const laidOut = await layoutWithELK(nn, ee, 'DOWN');
    setNodes(laidOut);
    setEdges(ee);
  }, [storyID, chapterID, setNodes, setEdges]);

  function normalizeValidationResponse(raw) {
    if (!raw) {
      return {
        perScene: {},
        graphIssues: [],
        canPublish: true,
        blockingCount: 0,
        warningCount: 0,
        message: t`No validation issues found.`,
      };
    }

    const perScene = {};
    const graphIssues = [];

    // Map backend issue.type → localized text.
    const translateIssueMessage = (issue, fallbackType) => {
      const type = issue?.type || fallbackType;

      switch (type) {
        case 'NO_SCENES':
          return t`Chapter has no scenes.`;
        case 'NO_START_SCENE':
          return t`No start scene could be determined for this chapter.`;
        case 'NO_END_SCENE':
          return t`No end scene could be determined for this chapter.`;
        case 'NO_PATH_START_TO_END':
          return t`There is no path from the start scene to the end scene.`;

        case 'DEAD_END_SCENE':
          return t`This scene is reachable from the start but cannot reach the end.`;
        case 'BROKEN_CHAIN_SCENE':
          return t`This scene can reach the end but is not reachable from the start. There is a missing or broken parent link.`;
        case 'MISSING_CHOICE_TEXT':
          return t`Choice has a target child but missing/empty display text.`;

        case 'STRANGLER_SCENE':
          return t`This scene is not part of the start–end graph (neither reachable from start nor connected to end).`;
        case 'MISSING_LINES':
          return t`Scene has missing or empty dialogue text in its lines.`;

        default:
          // Unknown/new type → fall back to backend text
          return issue?.reason || issue?.message || '';
      }
    };

    const addIssue = (sceneId, severity, message) => {
      if (!sceneId) return;
      const sid = String(sceneId);
      if (!perScene[sid]) {
        perScene[sid] = { status: 'ok', messages: [] };
      }

      // escalate severity: blocking > warning > ok
      const prev = perScene[sid].status || 'ok';
      if (severity === 'blocking') {
        perScene[sid].status = 'blocking';
      } else if (severity === 'warning' && prev !== 'blocking') {
        perScene[sid].status = 'warning';
      }

      if (message) {
        perScene[sid].messages.push(message);
      }
    };

    // ───────────────────────────────────────────────
    // 1) NEW SHAPE: errors / warnings / readyToPublish
    // ───────────────────────────────────────────────
    if (raw.errors && typeof raw.errors === 'object') {
      const { graph, deadEnds, brokenChainScenes, missingChoiceText } =
        raw.errors;

      // Chapter-level graph errors (no specific scene) → blocking
      if (Array.isArray(graph)) {
        graph.forEach((issue) => {
          graphIssues.push({
            ...issue,
            severity: 'blocking',
            message: translateIssueMessage(issue, issue?.type),
          });
        });
      }

      // Dead-ends: blocking (chapter-breaking)
      if (Array.isArray(deadEnds)) {
        deadEnds.forEach((issue) => {
          addIssue(
            issue.sceneId,
            'blocking',
            translateIssueMessage(issue, 'DEAD_END_SCENE')
          );
        });
      }

      // Broken parent chain: also blocking
      if (Array.isArray(brokenChainScenes)) {
        brokenChainScenes.forEach((issue) => {
          addIssue(
            issue.sceneId,
            'blocking',
            translateIssueMessage(issue, 'BROKEN_CHAIN_SCENE')
          );
        });
      }

      // Missing choice text but has a child → blocking (invalid choice)
      if (Array.isArray(missingChoiceText)) {
        missingChoiceText.forEach((issue) => {
          addIssue(
            issue.sceneId,
            'blocking',
            translateIssueMessage(issue, 'MISSING_CHOICE_TEXT')
          );
        });
      }
    }

    if (raw.warnings && typeof raw.warnings === 'object') {
      const { stranglers, missingLines } = raw.warnings;

      // Stranglers: warning (free / disconnected)
      if (Array.isArray(stranglers)) {
        stranglers.forEach((issue) => {
          addIssue(
            issue.sceneId,
            'warning',
            translateIssueMessage(issue, 'STRANGLER_SCENE')
          );
        });
      }

      // Missing lines: warning (content-level problem)
      if (Array.isArray(missingLines)) {
        missingLines.forEach((issue) => {
          addIssue(
            issue.sceneId,
            'warning',
            translateIssueMessage(issue, 'MISSING_LINES')
          );
        });
      }
    }

    // ───────────────────────────────────────────────
    // 2) LEGACY SHAPES: perScene / issues (keep support)
    // ───────────────────────────────────────────────
    if (raw.perScene && typeof raw.perScene === 'object') {
      Object.entries(raw.perScene).forEach(([sid, v]) => {
        const statusRaw = v.status || v.severity || v.level || 'ok';
        let severity = 'ok';
        if (['blocking', 'error', 'fatal'].includes(statusRaw)) severity = 'blocking';
        else if (['warn', 'warning'].includes(statusRaw)) severity = 'warning';

        const msgs = Array.isArray(v.messages || v.issues)
          ? (v.messages || v.issues)
          : v.message
            ? [v.message]
            : [];

        if (!msgs.length) {
          addIssue(sid, severity, null);
        } else {
          msgs.forEach((m) => addIssue(sid, severity, m));
        }
      });
    }

    if (Array.isArray(raw.issues)) {
      raw.issues.forEach((issue) => {
        const sid =
          issue.sceneId ??
          issue.sceneID ??
          issue.id ??
          issue.scene ??
          null;
        const statusRaw = issue.status || issue.severity || issue.level || 'ok';
        let severity = 'ok';
        if (['blocking', 'error', 'fatal'].includes(statusRaw)) severity = 'blocking';
        else if (['warn', 'warning'].includes(statusRaw)) severity = 'warning';

        addIssue(sid, severity, issue.message || null);
      });
    }

    // ───────────────────────────────────────────────
    // 3) Compute counts from perScene + graph issues
    // ───────────────────────────────────────────────
    let blockingCount = 0;
    let warningCount = 0;

    Object.values(perScene).forEach((entry) => {
      if (entry.status === 'blocking') blockingCount += 1;
      else if (entry.status === 'warning') warningCount += 1;
    });

    // Chapter-level graph errors also count as blocking
    blockingCount += graphIssues.length;

    // Decide canPublish:
    // prefer backend flags (readyToPublish / canPublish), then fallback
    let canPublish;
    if (typeof raw.readyToPublish === 'boolean') {
      canPublish = raw.readyToPublish;
    } else if (typeof raw.canPublish === 'boolean') {
      canPublish = raw.canPublish;
    } else {
      canPublish = blockingCount === 0;
    }

    // Build a human-friendly summary message
    let message = raw.message || raw.summary;
    if (!message) {
      if (blockingCount === 0 && warningCount === 0) {
        message = canPublish
          ? t`Chapter looks good for publishing.`
          : t`Chapter is not ready to publish, but no per-scene issues were reported.`;
      } else if (canPublish) {
        message = t`Chapter is publishable, but has ${warningCount} warning(s).`;
      } else {
        message = t`Found ${blockingCount} blocking issue(s) and ${warningCount} warning(s).`;
      }
    }

    return {
      perScene,
      graphIssues,
      canPublish,
      blockingCount,
      warningCount,
      message,
    };
  }



  const runValidation = React.useCallback(async () => {
    if (!storyID || !chapterID) return;

    try {
      setValidationRunning(true);
      setValidationError('');
      // 👇 adjust arg style if your API expects an object instead
      const raw = await validateInteractiveChapter(storyID, chapterID);
      const normalized = normalizeValidationResponse(raw);
      setValidationResult(normalized);
    } catch (e) {
      console.error('validateInteractiveChapter failed:', e);
      setValidationError(e?.message || t`Failed to validate chapter.`);
      setValidationResult(null);
    } finally {
      setValidationRunning(false);
    }
  }, [storyID, chapterID]);


  const createChild = React.useCallback(
    async (fromId, slot = 0) => {
      // Any change to the graph invalidates previous validation
      resetValidationUI();

      // ── Root-level free scene: called from the global FAB ──
      if (!fromId) {
        // If we somehow don't have a real chapter/story (e.g. mock mode),
        // fall back to local-only node so the editor still behaves.
        if (!storyID || !chapterID) {
          const id = newId();
          const data = { ...makeScene({ title: t`Scene ${id}` }), autoOpen: true };
          const node = {
            id,
            type: 'scene',
            position: { x: 0, y: 0 },
            data,
          };
          const nextNodes = [...nodes, node];
          setSelectedNodeId(id);
          reLayoutWith(nextNodes, edges);
          return;
        }

        try {
          // Show overlay while creating the scene on the backend
          setLoading(true);

          // Create "free" scene in Firestore first
          const { sceneId } = await createFreeInteractiveScene({
            storyID,
            chapterID,
            data: {}, // backend will set default title/index/etc.
          });

          // Reload chapter graph from backend so editor is fully in sync
          await refreshFromBackend();

          // Select the newly created scene and ask the SceneNode to auto-open its editor
          if (sceneId) {
            const sid = String(sceneId);
            setSelectedNodeId(sid);
            setNodes((prev) =>
              prev.map((n) =>
                n.id === sid
                  ? { ...n, data: { ...(n.data || {}), autoOpen: true } }
                  : n
              )
            );
          }
        } catch (err) {
          console.error('createFreeScene failed:', err);
        } finally {
          setLoading(false);
        }

        return;
      }

      // ── Normal "child" creation from a parent node (existing behavior) ──
      if (!canAddChildFrom(fromId)) return;

      const id = newId();
      const data = makeScene({ title: t`Scene ${id}` });
      const parent = nodes.find((n) => n.id === fromId);
      const { x = 0, y = 0 } = parent?.position || {};
      const pos = { x: x + (slot - 1) * 220, y: y + 160 };

      const node = { id, type: 'scene', position: pos, data };
      const nextNodes = [...nodes, node];
      const nextEdges = addEdge({ source: fromId, target: id }, edges);
      setSelectedNodeId(id);
      reLayoutWith(nextNodes, nextEdges);
    },
    [
      storyID,
      chapterID,
      nodes,
      edges,
      canAddChildFrom,
      reLayoutWith,
      refreshFromBackend,
      setLoading,
      setNodes,
      setSelectedNodeId,
      resetValidationUI,
    ]
  );


  // Build adjacency for cycle/descendant checks
  const adj = React.useMemo(() => {
    const m = new Map();
    nodes.forEach(n => m.set(n.id, []));
    edges.forEach(e => {
      if (m.has(e.source)) m.get(e.source).push(e.target);
    });
    return m;
  }, [nodes, edges]);

  const isDescendant = React.useCallback((rootId, maybeDescId) => {
    if (rootId === maybeDescId) return true;
    const seen = new Set([rootId]);
    const stack = [...(adj.get(rootId) || [])];
    while (stack.length) {
      const cur = stack.pop();
      if (cur === maybeDescId) return true;
      if (!seen.has(cur)) {
        seen.add(cur);
        (adj.get(cur) || []).forEach(n => stack.push(n));
      }
    }
    return false;
  }, [adj]);

  const computeEligibility = React.useCallback(
    (fromId, extraBlockedIds) => {
      const eligible = new Set();
      const ineligible = new Set();
      const extraBlocked =
        extraBlockedIds && extraBlockedIds.size
          ? extraBlockedIds
          : null;

      nodes.forEach((n) => {
        const tid = n.id;
        const isExtraBlocked = extraBlocked?.has?.(tid);
        const bad =
          tid === fromId ||              // no self-link
          isDescendant(tid, fromId) ||   // no cycles
          !!isExtraBlocked;              // no duplicate targets in other rows

        (bad ? ineligible : eligible).add(tid);
      });

      return { eligible, ineligible };
    },
    [nodes, isDescendant]
  );



  const updateScene = React.useCallback((id, patch) => {
    const nextNodes = nodes.map(n => n.id === id ? { ...n, data: { ...n.data, ...patch } } : n);
    reLayoutWith(nextNodes, edges);
  }, [nodes, edges, reLayoutWith]);

  const saveScene = React.useCallback(
    async (fromId, payload) => {
      if (!storyID || !chapterID) return;

      // Persisted change → validation should be cleared
      resetValidationUI();

      try {
        setNodes((prev) =>
          prev.map((n) =>
            n.id === fromId ? { ...n, data: { ...n.data, ...payload } } : n
          )
        );
        await saveInteractiveScene({
          storyID,
          chapterID,
          sceneID: fromId,
          data: payload,
        });
        await refreshFromBackend();
      } catch (e) {
        console.error('saveScene failed:', e);
      }
    },
    [storyID, chapterID, setNodes, refreshFromBackend, resetValidationUI]
  );


  const deleteScene = React.useCallback(
    async (sceneID) => {
      if (!storyID || !chapterID || !sceneID) return;

      // Structure changed → invalidate old validation
      resetValidationUI();

      try {
        setLoading(true);
        await deleteInteractiveScene({ storyID, chapterID, sceneID });
        // Bring graph back in sync with backend
        await refreshFromBackend();
        setSelectedNodeId((prev) => (prev === sceneID ? null : prev));
      } catch (e) {
        console.error('deleteScene failed:', e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [storyID, chapterID, refreshFromBackend, setLoading, resetValidationUI]
  );

  const onNodeClick = React.useCallback(
    (_e, node) => {
      // If we’re in pick mode, keep existing behavior
      if (pickState.active) {
        const nid = node.id;
        if (pickState.eligible.has(nid)) {
          const cb = pickState.onPick;
          setPickState({
            active: false,
            fromId: null,
            onPick: null,
            onCancel: null,
            eligible: new Set(),
            ineligible: new Set(),
          });
          try {
            cb?.(nid);
          } catch {
            /* noop */
          }
        }
        return;
      }

      // Normal click → open/focus scene AND clear validation markers
      resetValidationUI();
      setSelectedNodeId(node.id);
    },
    [pickState, resetValidationUI]
  );



  const onConnectStart = React.useCallback((_e, params) => { connectingFrom.current = params?.nodeId || null; }, []);
  const onConnectEnd = React.useCallback((e) => {
    const isPane = e.target?.classList?.contains('react-flow__pane');
    if (!isPane || !connectingFrom.current) return;
    const src = connectingFrom.current;
    if (!canAddChildFrom(src)) { connectingFrom.current = null; return; }

    const getClientXY = (evt) => (evt.changedTouches?.[0])
      ? { x: evt.changedTouches[0].clientX, y: evt.changedTouches[0].clientY }
      : { x: evt.clientX, y: evt.clientY };
    const pos = rf.screenToFlowPosition(getClientXY(e));

    const id = newId();
    const node = { id, type: 'scene', position: pos, data: makeScene({ title: t`Scene ${id}` }) };
    const nextNodes = [...nodes, node];
    const nextEdges = addEdge({ source: src, target: id }, edges);
    connectingFrom.current = null;
    reLayoutWith(nextNodes, nextEdges);
  }, [rf, nodes, edges, canAddChildFrom, reLayoutWith]);

  const deleteSelectedNode = React.useCallback(() => {
    if (!selectedNodeId) return;
    const nextEdges = edges.filter(e => e.source !== selectedNodeId && e.target !== selectedNodeId);
    const nextNodes = nodes.filter(n => n.id !== selectedNodeId);
    setSelectedNodeId(null);
    reLayoutWith(nextNodes, nextEdges);
  }, [nodes, edges, selectedNodeId, reLayoutWith]);

  const focusNode = React.useCallback((id) => {
    const n = nodes.find(n => n.id === id);
    if (!n) return;
    rf.setCenter(
      n.position.x + (n.width ?? 200) / 2,
      n.position.y + (n.height ?? 80) / 2,
      { zoom: 1.1, duration: 300 }
    );
    setSelectedNodeId(id);
  }, [nodes, rf]);


  // ─── Neighbor highlight (parents/children of the selected node) ───
  const neighborInfo = React.useMemo(() => {
    if (!selectedNodeId) return { parents: new Set(), children: new Set(), hiEdges: new Set(), any: false };
    const parents = new Set(edges.filter(e => e.target === selectedNodeId).map(e => e.source));
    const children = new Set(edges.filter(e => e.source === selectedNodeId).map(e => e.target));
    const hiEdges = new Set(edges.filter(e => e.source === selectedNodeId || e.target === selectedNodeId).map(e => e.id));
    return { parents, children, hiEdges, any: true };
  }, [edges, selectedNodeId]);

  // Keep highlight: bold edges touching selection; dim others
  const styledEdges = React.useMemo(() => {
    if (!neighborInfo.any) return edges;
    return edges.map(e =>
      neighborInfo.hiEdges.has(e.id)
        ? { ...e, style: { ...(e.style || {}), strokeWidth: 2.5 } }
        : { ...e, style: { ...(e.style || {}), opacity: 0.35 } }
    );
  }, [edges, neighborInfo]);

  const ctxValue = React.useMemo(
    () => ({
      getHighlight: (nodeId) => {
        if (!selectedNodeId) return "none";
        if (nodeId === selectedNodeId) return "selected";
        if (neighborInfo.parents.has(nodeId)) return "parent";
        if (neighborInfo.children.has(nodeId)) return "child";
        return "none";
      },
      hasSelection: !!selectedNodeId,
      selectedNodeId,
      maxChildren: MAX_CHILDREN,
      getChildCount,
      canAddChildFrom,
      createChild,
      updateScene,
      saveScene,
      storyID,
      characters,
      isDescendant,
      getParentCount,
      deleteScene,
      getAllScenes: () =>
        nodes.map((n) => ({ id: n.id, title: n.data?.title || n.id })),

      ensureGraphView: () => {
        setView((prev) => (prev === "graph" ? prev : "graph"));
      },

      beginPick: (fromId, onPick, options) => {
        if (!fromId || typeof onPick !== "function") return;

        const extraBlockedIds = options?.extraBlockedIds
          ? new Set(options.extraBlockedIds.map(String))
          : undefined;

        const { eligible, ineligible } = computeEligibility(
          fromId,
          extraBlockedIds
        );

        setPickState({
          active: true,
          fromId,
          onPick,
          onCancel:
            typeof options?.onCancel === "function" ? options.onCancel : null,
          eligible,
          ineligible,
        });
      },
      cancelPick: () =>
        setPickState((prev) => {
          const cb = prev.onCancel;
          if (typeof cb === "function") {
            try {
              cb();
            } catch {
              /* noop */
            }
          }
          return {
            active: false,
            fromId: null,
            onPick: null,
            onCancel: null,
            eligible: new Set(),
            ineligible: new Set(),
          };
        }),
      getPickState: (nodeId) => {
        if (!pickState.active) return "none";
        if (pickState.eligible.has(nodeId)) return "ok";
        if (pickState.ineligible.has(nodeId)) return "no";
        return "none";
      },

      refreshCharacters: async () => {
        if (!storyID) return;
        try {
          const list = await getCharacters(storyID);
          const normalized = (Array.isArray(list) ? list : [])
            .map((c) => ({
              id: String(c.id ?? c.characterId ?? c.uid ?? ""),
              name: c.name ?? t`Unnamed`,
              head: c.head ?? null,
              hair: c.hair ?? null,
              hairColor: c.hairColor ?? null,
            }))
            .filter((c) => c.id);
          setCharacters(normalized);
        } catch {
          /* ignore */
        }
      },

      // 🔍 Validation helpers for SceneComponent
      getValidation: (nodeId) => validationResult?.perScene?.[nodeId] || null,
      validationSummary: validationResult,

      // ⭐ NEW: live-preview connections → update edges + layout immediately
      previewSceneConnections: (sceneId, connections) => {
        const sid = String(sceneId);

        // Only build edges for existing targets with a childId
        const newEdgesForScene = [];
        (connections || []).forEach((c) => {
          if (c.type === "existing" && c.childId) {
            const tid = String(c.childId);
            if (tid !== sid) {
              newEdgesForScene.push({
                id: `${sid}->${tid}`,
                source: sid,
                target: tid,
              });
            }
          }
        });

        const remaining = edges.filter((e) => e.source !== sid);
        const nextEdges = [...remaining, ...newEdgesForScene];

        // Use existing layout helper to keep everything tidy
        reLayoutWith(nodes, nextEdges);
      },

      // (optionally usable from SceneComponent for hard resync)
      refreshFromBackend,
    }),
    [
      MAX_CHILDREN,
      getChildCount,
      canAddChildFrom,
      createChild,
      updateScene,
      saveScene,
      storyID,
      characters,
      nodes,
      edges,
      computeEligibility,
      getParentCount,
      deleteScene,
      pickState.active,
      pickState.eligible,
      pickState.ineligible,
      selectedNodeId,
      neighborInfo,
      isDescendant,
      validationResult,
      reLayoutWith,
      refreshFromBackend,
    ]
  );



  function mapChapterToGraph(chapter) {
    const scenes = Array.isArray(chapter?.scenes) ? chapter.scenes : [];
    const edges = Array.isArray(chapter?.edges) ? chapter.edges : [];

    // Optional: chapter-level start/end scene IDs
    const startId = chapter?.startSceneId ? String(chapter.startSceneId) : null;
    const endId = chapter?.endSceneId ? String(chapter.endSceneId) : null;

    const scenesById = new Map(scenes.map(s => [String(s.id), s]));
    const edgesBySource = new Map();
    edges.forEach(e => {
      const src = String(e.source ?? e.from ?? '');
      const arr = edgesBySource.get(src) || [];
      arr.push(e);
      edgesBySource.set(src, arr);
    });

    const nodes = scenes.map(s => {
      const sid = String(s.id);
      const kids = getOrderedChildren(s, scenesById, edgesBySource);
      const choices = Array.isArray(s.choices) ? s.choices : [];
      const mode = s.mode || (kids.length === 0 ? 'none' : kids.length === 1 ? 'continue' : 'choices');

      // Prefer scene-local flags; fallback to chapter-level IDs
      const isEntry = typeof s.entryScene === 'boolean'
        ? s.entryScene
        : (startId && sid === startId);
      const isLast = typeof s.lastScene === 'boolean'
        ? s.lastScene
        : (endId && sid === endId);

      let connections = [];
      if (mode === 'none') {
        connections = [];
      } else if (mode === 'continue') {
        if (s.continue?.type === 'new' && !kids.length) {
          connections = [{ type: 'new', childId: '', text: '' }];
        } else if (kids.length === 1) {
          connections = [{ type: 'existing', childId: String(kids[0]), text: '' }];
        } else {
          connections = [{ type: 'new', childId: '', text: '' }];
        }
      } else {
        connections = kids.slice(0, 3).map((cid, i) => ({
          type: 'existing',
          childId: String(cid),
          text: choices[i]?.text || '',
        }));
        if (!kids.length && choices.length) {
          connections = choices.slice(0, 3).map(ch => ({
            type: ch.type === 'existing' && ch.childId ? 'existing' : 'new',
            childId: ch.childId ? String(ch.childId) : '',
            text: ch.text || '',
          }));
        }
      }

      return {
        id: sid,
        type: 'scene',
        position: { x: 0, y: 0 },
        data: {
          title:
            s.title ||
            s.key ||
            (s.index != null ? t`Scene ${s.index}` : t`Scene`),
          summary: s.summary || '',
          background: Number.isFinite(s.background) ? s.background : undefined,
          lines: Array.isArray(s.lines) ? s.lines : undefined,
          mode: s.mode || (kids.length === 0 ? 'none' : kids.length === 1 ? 'continue' : 'choices'),
          continue: s.continue || undefined,
          choices: Array.isArray(s.choices) ? s.choices : undefined,
          connections,
          entryScene: !!isEntry,
          lastScene: !!isLast,
        },
      };
    });

    const have = new Set(nodes.map(n => n.id));
    const out = [];
    if (edges.length) {
      edges.forEach(e => {
        const source = String(e.source ?? e.from ?? '');
        const target = String(e.target ?? e.to ?? '');
        if (have.has(source) && have.has(target)) {
          out.push({ id: `${source}->${target}`, source, target, label: e.label ?? undefined });
        }
      });
    } else {
      scenes.forEach(s => {
        const src = String(s.id);
        const children = Array.isArray(s.children) ? s.children : [];
        children.forEach(t => {
          const tgt = String(t);
          if (have.has(src) && have.has(tgt)) out.push({ id: `${src}->${tgt}`, source: src, target: tgt });
        });
      });
    }

    return { nodes, edges: out };
  }
  const listOrder = React.useMemo(() => {
    if (!nodes.length) {
      return { start: null, startSide: [], endSide: [], end: null, orphans: [], showBreak: false };
    }

    const nodeMap = new Map(nodes.map((n) => [n.id, n]));

    // Find START (entryScene), fallback = first node
    const start =
      nodes.find((n) => n.data?.entryScene) ||
      nodes[0] ||
      null;

    // Find END (lastScene), optional
    const end = nodes.find((n) => n.data?.lastScene) || null;

    // ── Reachable FROM START (forward BFS via adj) ──
    const reachableFromStart = new Set();
    if (start) {
      const q = [start.id];
      reachableFromStart.add(start.id);

      while (q.length) {
        const id = q.shift();
        const children = adj.get(id) || [];
        children.forEach((cid) => {
          if (!reachableFromStart.has(cid) && nodeMap.has(cid)) {
            reachableFromStart.add(cid);
            q.push(cid);
          }
        });
      }
    }

    // ── Reachable TO END (reverse BFS) ──
    const revAdj = new Map();
    nodes.forEach((n) => revAdj.set(n.id, []));
    edges.forEach((e) => {
      const src = e.source;
      const tgt = e.target;
      if (revAdj.has(tgt)) {
        revAdj.get(tgt).push(src);
      }
    });

    const reachableToEnd = new Set();
    if (end) {
      const q = [end.id];
      reachableToEnd.add(end.id);

      while (q.length) {
        const id = q.shift();
        const parents = revAdj.get(id) || [];
        parents.forEach((pid) => {
          if (!reachableToEnd.has(pid) && nodeMap.has(pid)) {
            reachableToEnd.add(pid);
            q.push(pid);
          }
        });
      }
    }

    const startSide = [];
    const endSide = [];
    const orphans = [];

    // Classify nodes based on reachability
    nodes.forEach((n) => {
      if (start && n.id === start.id) return;
      if (end && n.id === end.id) return;

      const fromStart = start && reachableFromStart.has(n.id);
      const toEnd = end && reachableToEnd.has(n.id);

      if (fromStart) {
        // Main chain from START side (including nodes also leading to END)
        startSide.push(n);
      } else if (toEnd) {
        // Connected only to END side
        endSide.push(n);
      } else {
        // True stranglers: not on START side and not on END side
        orphans.push(n);
      }
    });

    // Need a break if:
    // - We have an END and
    //   - it's not reachable from START OR
    //   - we have any end-side-only nodes
    const showBreak =
      !!end &&
      (
        !(start && reachableFromStart.has(end.id)) ||
        endSide.length > 0
      );

    return { start, startSide, endSide, end, orphans, showBreak };
  }, [nodes, edges, adj]);

  const { start, startSide, endSide, end, orphans, showBreak } = listOrder;

  // Load characters once per story
  React.useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!storyID) { setCharacters([]); return; }
      try {
        setCharsError('');
        const list = await getCharacters(storyID);
        if (cancelled) return;
        const normalized = (Array.isArray(list) ? list : []).map(c => ({
          id: String(c.id ?? c.characterId ?? c.uid ?? ''),
          name: c.name ?? t`Unnamed`,
          head: c.head ?? null,
          hair: c.hair ?? null,
          hairColor: c.hairColor ?? null,
        })).filter(c => c.id);
        setCharacters(normalized);
      } catch (e) {
        if (!cancelled) setCharsError(e?.message || t`Failed to load characters`);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [storyID]);

  const saveTitle = React.useCallback(async () => {
    const next = chapterTitle.trim();
    if (!storyID || !chapterID) return;
    if (!next || next === serverTitle) return;
    try {
      setSavingTitle(true);
      setTitleError('');
      await updateInteractiveChapterTitle(storyID, chapterID, next);
      setServerTitle(next);
    } catch (e) {
      setTitleError(e?.message || t`Failed to save chapter title`);
    } finally {
      setSavingTitle(false);
    }
  }, [chapterTitle, serverTitle, storyID, chapterID]);

  const renderSceneListItem = (n) => {
    const v = validationResult?.perScene?.[n.id];
    const secondaryText = v?.messages?.length
      ? v.messages[0]
      : n.data?.summary;

    const status = v?.status;

    return (
      <React.Fragment key={n.id}>
        <ListItem
          alignItems="flex-start"
          secondaryAction={
            <Stack direction="row" spacing={1} alignItems="center">
              {n.data?.entryScene && (
                <Chip size="small" label={t`START`} />
              )}
              {n.data?.lastScene && (
                <Chip size="small" label={t`END`} />
              )}

              <Chip
                size="small"
                label={`${(outgoing.get(n.id) || []).length} →`}
              />
              <IconButton
                edge="end"
                onClick={() => {
                  setView('graph');
                  setTimeout(() => focusNode(n.id), 0);
                }}
              >
                <ShareIcon />
              </IconButton>
            </Stack>
          }
          onClick={() => setSelectedNodeId(n.id)}
          selected={selectedNodeId === n.id}
          sx={(t) => {
            let borderColor = 'transparent';

            if (validationResult) {
              if (!v || status === 'ok') {
                // ✅ Good scene (no issues for this node)
                borderColor = t.palette.success.main;
              } else if (status === 'blocking') {
                // 🔴 Blocking issues
                borderColor = t.palette.error.main;
              } else if (status === 'warning') {
                // 🟡 Warnings only
                borderColor = t.palette.warning.main;
              }
            }

            return {
              borderLeftWidth: 3,
              borderLeftStyle: 'solid',
              borderLeftColor: borderColor,
              pl: borderColor !== 'transparent' ? 1.25 : undefined,
            };
          }}
        >
          <ListItemText
            primary={n.data?.title || n.id}
            secondary={secondaryText}
            sx={{
              pr: { xs: 7, sm: 9 }, // so text doesn't go under chips/badge
              '& .MuiListItemText-primary': {
                wordBreak: 'break-word',
              },
              '& .MuiListItemText-secondary': {
                wordBreak: 'break-word',
              },
            }}
          />
        </ListItem>
        <Divider component="li" />
      </React.Fragment>
    );
  };


  return (
    <Stack spacing={1.5} sx={{ height: '100%', minHeight: 520 }}>
      {/* Header */}
      <Box sx={{ position: 'relative' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
        >
          <TextField
            value={chapterTitle}
            onChange={(e) => setChapterTitle(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            variant="outlined"
            size="small"
            placeholder={t`Untitled chapter`}
            InputProps={{
              endAdornment: savingTitle ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null,
            }}
            sx={{
              flex: 1,
              mr: { xs: 0, sm: 2 },
              mb: { xs: 1, sm: 0 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'text.secondary' },
                '&.Mui-focused fieldset': {
                  borderColor: 'callToAction.main',
                  borderWidth: 2,
                },
              },
              '& .MuiInputBase-input': (t) => ({
                fontSize: t.typography.h6.fontSize,
                fontWeight: t.typography.fontWeightMedium,
                lineHeight: 1.25,
                padding: t.spacing(1.25, 1.5),
              }),
            }}
          />

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flexShrink: 0,
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              rowGap: 0.5,
            }}
          >
            {loadError && (
              <Typography variant="body2" color="error">
                {loadError}
              </Typography>
            )}
            {validationError && (
              <Typography variant="body2" color="error">
                {validationError}
              </Typography>
            )}

            {/* Validate */}
            <Button
              size={isMobile ? 'small' : 'medium'}
              variant="outlined"
              color={validationRunning ? 'primary' : 'inherit'}
              disabled={validationRunning || !storyID || !chapterID}
              onClick={runValidation}
              sx={{
                minWidth: isMobile ? 80 : 140,
                justifyContent: 'center',
                px: 1,
                '& .MuiCircularProgress-root': {
                  mr: 1,
                },
              }}
            >
              {validationRunning && (
                <CircularProgress
                  size={18}
                  thickness={5}
                  color="inherit"
                />
              )}
              <Box
                component="span"
                sx={(t) => ({
                  ml: validationRunning ? 0 : 0,
                  fontWeight: 600,
                  letterSpacing: 0.2,
                  color: t.palette.text.primary,
                  display: 'inline-flex',
                  alignItems: 'center',
                })}
              >
                {validationRunning ? t`Loading…` : t`Validate`}
              </Box>
            </Button>

            {/* Preview */}
            <Button
              size={isMobile ? 'small' : 'medium'}
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => setPreviewOpen(true)}
            >
              {t`Preview`}
            </Button>

            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, v) => {
                if (pickState.active) return;
                if (v) setView(v);
              }}
              size={isMobile ? 'small' : 'medium'}
              sx={(t) => ({
                // keep group visually aligned with normal Buttons
                '& .MuiToggleButton-root': {
                  // match typical MUI Button heights
                  minHeight: isMobile ? 32 : 40,
                  paddingInline: isMobile ? t.spacing(1.2) : t.spacing(1.6),
                  paddingBlock: isMobile ? t.spacing(0.5) : t.spacing(0.75),
                  fontWeight: 400,
                },
              })}
            >
              <ToggleButton value="graph">
                <ShareIcon sx={{ mr: 1 }} />
                {t`Graph`}
              </ToggleButton>
              <ToggleButton value="list">
                <FormatListBulletedIcon sx={{ mr: 1 }} />
                {t`List`}
              </ToggleButton>
            </ToggleButtonGroup>

          </Stack>
        </Stack>

        {pickState.active && (
          <Box
            onClick={() => ctxValue.cancelPick?.()}
            sx={(t) => ({
              position: 'absolute',
              inset: 0,
              bgcolor: t.palette.action.disabledBackground,
              opacity: 0.8,
              zIndex: 5,
              cursor: 'not-allowed',
            })}
          />
        )}
      </Box>

      {validationResult && (
        <Box
          sx={(t) => {
            const hasBlocking = (validationResult.blockingCount || 0) > 0;
            const hasWarnings = (validationResult.warningCount || 0) > 0;

            let borderColor;
            let bgBase;

            if (hasBlocking) {
              // 🔴 blocking issues
              borderColor = t.palette.error.main;
              bgBase = t.palette.error.light;
            } else if (hasWarnings) {
              // 🟡 only warnings
              borderColor = t.palette.warning.main;
              bgBase = t.palette.warning.light;
            } else {
              // 🟢 clean
              borderColor = t.palette.success.main;
              bgBase = t.palette.success.light;
            }

            return {
              mt: 0.5,
              mb: 0.75,
              px: { xs: 1, sm: 1.25 },
              py: { xs: 0.5, sm: 0.75 },
              borderRadius: 1.5,
              border: `1px solid ${borderColor}`,
              bgcolor: `${bgBase}22`,
            };
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb:
                validationResult.blockingCount ||
                  validationResult.warningCount
                  ? 0.25
                  : 0,
            }}
          >
            {validationResult.message}
          </Typography>

          {(validationResult.blockingCount ||
            validationResult.warningCount) && (
              <Typography variant="caption">
                {t`${validationResult.blockingCount} blocking issue(s), ${validationResult.warningCount} warning(s).`}

              </Typography>
            )}
        </Box>
      )}


      {/* Body */}
      <Box sx={{ height: '70vh', flex: 1, minHeight: 420, border: t => `1px solid ${t.palette.divider}`, borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
            <Typography variant="body2">{t`Loading…`}</Typography>
          </Box>
        )}
        {view === 'graph' ? (
          <GraphCtx.Provider value={ctxValue}>
            <Box
              sx={(t) => ({
                height: '100%',

                '& .react-flow__controls': {
                  backgroundColor: t.palette.background.paper,
                  borderRadius: 1.5,
                  border: `1px solid ${t.palette.divider}`,
                  padding: t.spacing(0.75),
                  boxShadow:
                    t.palette.mode === 'dark'
                      ? '0 2px 10px rgba(0,0,0,0.6)'
                      : '0 2px 10px rgba(0,0,0,0.15)',
                },

                // ⬇️ UPDATED: make the buttons small & tight
                '& .react-flow__controls-button': {
                  width: 28,
                  height: 28,
                  padding: 0,
                  margin: t.spacing(0.25, 0),
                  backgroundColor: 'transparent',
                  color: t.palette.text.primary,
                  borderRadius: 0.75,
                  border: 'none',
                  boxShadow: 'none',
                  fontSize: '0.75rem',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition:
                    'background-color 120ms ease-out, transform 80ms ease-out, box-shadow 80ms ease-out',

                  '& svg': {
                    fontSize: 16,
                  },

                  '&:hover': {
                    backgroundColor: t.palette.action.hover,
                    transform: 'translateY(-1px)',
                    boxShadow:
                      t.palette.mode === 'dark'
                        ? '0 1px 4px rgba(0,0,0,0.6)'
                        : '0 1px 4px rgba(0,0,0,0.2)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: 'none',
                  },
                },
              })}
            >
              <ReactFlow
                nodes={nodes}
                edges={styledEdges}
                defaultEdgeOptions={defaultEdgeOptions}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                nodesDraggable={false}
                nodesConnectable={false}
                connectOnClick={false}
                fitView
              >
                <MiniMap
                  style={{
                    background: theme.palette.background.default,
                    borderRadius: 8,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
                <Controls
                  showInteractive={false}
                  position="bottom-left"
                />
                <Background />
              </ReactFlow>

              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 10,
                }}
              >
                <Fab
                  color="primary"
                  size={isMobile ? 'medium' : 'small'}
                  onClick={() => createChild(null)}
                >
                  <AddIcon />
                </Fab>
              </Box>

              {pickState.active && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: 'background.paper',
                    border: (t) => `1px solid ${t.palette.divider}`,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    boxShadow: 2,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">
                    {t`Select a scene on the graph (green = selectable, red = blocked)`}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => ctxValue.cancelPick?.()}
                  >
                    {t`Cancel`}
                  </Button>
                </Box>
              )}
            </Box>
          </GraphCtx.Provider>
        ) : (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <List dense={isMobile}>
              {/* START */}
              {start && renderSceneListItem(start)}

              {/* Nodes on START side (reachable from START, excluding START/END) */}
              {startSide.map((n) => renderSceneListItem(n))}

              {/* Break in main chain (vertical 3 dots before END side) */}
              {end && showBreak && (
                <>
                  <ListItem
                    disabled
                    sx={{
                      justifyContent: 'center',
                      opacity: 0.6,
                      py: 0.5,
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </ListItem>
                  <Divider component="li" />
                </>
              )}

              {/* Nodes on END side only (connected to END but not to START) */}
              {endSide.map((n) => renderSceneListItem(n))}

              {/* END */}
              {end && renderSceneListItem(end)}

              {/* Stranglers / orphans at the very end, with a simple separator */}
              {orphans.length > 0 && (
                <>
                  <ListItem
                    disabled
                    sx={{
                      opacity: 0.7,
                      py: 0.5,
                    }}
                  >
                    <ListItemText
                      primary={t`Other scenes (not connected to START or END)`}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />

                  </ListItem>
                  <Divider component="li" />
                  {orphans.map((n) => renderSceneListItem(n))}
                </>
              )}
            </List>
          </Box>
        )}


        {/* Floating actions */}
        {/* {view === 'graph' && (
          <Stack
            sx={{ position: 'absolute', right: 12, bottom: 12 }}
            spacing={1.25}
            alignItems="flex-end"
          >
            <Fab
              color="default"
              size={isMobile ? 'medium' : 'small'}
              disabled={!selectedNodeId}
              onClick={deleteSelectedNode}
            >
              <DeleteOutlineIcon />
            </Fab>
          </Stack>
        )} */}

        <InteractiveChapterPreviewLauncher
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          storyID={storyID}
          chapterID={chapterID}
        />
      </Box>
    </Stack>
  );
}
